import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { VerifyCheckoutExtensionInstalledQuery } from "#web/components/shop/queries/verifyCheckoutExtensionInstalledQuery";
import { i18nLike } from "#web/services/i18nLike";
import { fetchTinybird, parsePipeResult } from "#web/services/tinybird";
import {
  Badge,
  Banner,
  Box,
  Card,
  IndexTable,
  InlineStack,
  SkeletonBodyText,
  Text,
  Tooltip,
  Icon,
} from "@shopify/polaris";
import { queryOptions, useQuery } from "@tanstack/react-query";
import * as v from "valibot";
import { InfoIcon } from "@shopify/polaris-icons";

const en = {
  blockedCodesTable: {
    headings: {
      couponCode: "Coupon Code",
      blockedCodes: "Blocked Codes",
      firstBlocked: "First Blocked",
      mostRecentBlocked: "Most Recent Blocked",
      uniqueVisitorsProtected: "Unique Visitors Protected",
    },
    tooltips: {
      blockedCodes: "Number of times this coupon code was blocked",
      uniqueVisitorsProtected: "Number of unique visitors who were prevented from using this code",
    },
    insufficientData: "Insufficient data. May take a few hours/days to appear.",
    resourceName: {
      plural: "Coupons",
      singular: "Coupon",
    },
  },
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "There was an error loading the blocked codes table",
  },
  title: "Blocked Codes",
};

const BlockedCodesTableQuery = ({
  endDate,
  installed,
  shopId,
  startDate,
}: {
  endDate: string;
  installed: boolean;
  shopId: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: installed && !!shopId,
    queryFn: () => {
      const BlockedCodesTableData = v.array(
        v.object({
          _first_injected: v.optional(v.nullable(v.string())),
          _last_injected: v.optional(v.nullable(v.string())),
          code: v.optional(v.nullable(v.string())),
          count: v.optional(v.nullable(v.number())),
          first_injected: v.optional(v.nullable(v.string())),
          last_injected: v.optional(v.nullable(v.string())),
          user_count: v.optional(v.nullable(v.number())),
        }),
      );
      return fetchTinybird("blocked_codes_table", { endDate, shopId: shopId!, startDate }).then(
        parsePipeResult(BlockedCodesTableData),
      );
    },
    queryKey: ["blocked_codes_table", shopId, startDate, endDate],
  });

const BlockedCodesTableTrackedQuery = ({
  endDate,
  installed,
  shopId,
  startDate,
}: {
  endDate: string;
  installed: boolean;
  shopId: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: !installed && !!shopId,
    queryFn: () => {
      const BlockedCodesTableTrackedData = v.array(
        v.object({
          _first_injected: v.optional(v.nullable(v.string())),
          _last_injected: v.optional(v.nullable(v.string())),
          code: v.optional(v.nullable(v.string())),
          count: v.optional(v.nullable(v.number())),
          first_injected: v.optional(v.nullable(v.string())),
          last_injected: v.optional(v.nullable(v.string())),
          user_count: v.optional(v.nullable(v.number())),
        }),
      );
      return fetchTinybird("blocked_codes_table_tracked", { endDate, shopId: shopId!, startDate }).then(
        parsePipeResult(BlockedCodesTableTrackedData),
      );
    },
    queryKey: ["blocked_codes_table_tracked", shopId, startDate, endDate],
  });

interface BlockedCodesTableProps {
  dateRange: {
    end: string;
    start: string;
  };
}

export const BlockedCodesTable = ({ dateRange }: BlockedCodesTableProps) => {
  const t = i18nLike(en);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const {
    data: checkoutExtensionData,
    isError: checkoutExtensionError,
    isFetching: checkoutExtensionFetching,
  } = useQuery(VerifyCheckoutExtensionInstalledQuery({ shopId: shop?.id }));
  const {
    data: blockedCodes,
    isError: blockedCodesError,
    isFetching: blockedCodesFetching,
  } = useQuery(
    BlockedCodesTableQuery({
      endDate: dateRange.end,
      installed: !!checkoutExtensionData?.installed,
      shopId: shop?.id,
      startDate: dateRange.start,
    }),
  );
  const {
    data: trackedCodes,
    isError: trackedCodesError,
    isFetching: trackedCodesFetching,
  } = useQuery(
    BlockedCodesTableTrackedQuery({
      endDate: dateRange.end,
      installed: !!checkoutExtensionData?.installed,
      shopId: shop?.id,
      startDate: dateRange.start,
    }),
  );

  const data = (checkoutExtensionData?.installed ? blockedCodes?.data : trackedCodes?.data) ?? [];
  const isError = shopError || checkoutExtensionError || blockedCodesError || trackedCodesError;
  const isLoading = shopFetching || checkoutExtensionFetching || blockedCodesFetching || trackedCodesFetching;

  if (isError) {
    return (
      <Banner title={t("error.title")} tone="critical">
        {t("error.description")}
      </Banner>
    );
  }

  return (
    <Card padding="0">
      <Box background="bg-surface-secondary" padding="400">
        <Text as="h2" variant="headingMd">
          {t("title")}
        </Text>
      </Box>
      {isLoading ? (
        <Box padding="400">
          <SkeletonBodyText />
        </Box>
      ) : (
        <IndexTable
          emptyState={
            <InlineStack align="center">
              <Box padding="400">
                <Badge>{t("blockedCodesTable.insufficientData")}</Badge>
              </Box>
            </InlineStack>
          }
          headings={[
            { title: t("blockedCodesTable.headings.couponCode") },
            {
              title: (
                <InlineStack gap="100" blockAlign="center">
                  <span>{t("blockedCodesTable.headings.blockedCodes")}</span>
                  <Tooltip content={t("blockedCodesTable.tooltips.blockedCodes")}>
                    <Icon source={InfoIcon} tone="base" />
                  </Tooltip>
                </InlineStack>
              ),
            },
            {
              title: (
                <InlineStack gap="100" blockAlign="center">
                  <span>{t("blockedCodesTable.headings.uniqueVisitorsProtected")}</span>
                  <Tooltip content={t("blockedCodesTable.tooltips.uniqueVisitorsProtected")}>
                    <Icon source={InfoIcon} tone="base" />
                  </Tooltip>
                </InlineStack>
              ),
            },
            { title: t("blockedCodesTable.headings.firstBlocked") },
            { title: t("blockedCodesTable.headings.mostRecentBlocked") },
          ]}
          itemCount={data.length}
          resourceName={{
            plural: t("blockedCodesTable.resourceName.plural"),
            singular: t("blockedCodesTable.resourceName.singular"),
          }}
          selectable={false}
        >
          {data.map(({ code, count, first_injected, last_injected, user_count }, index) => (
            <IndexTable.Row id={code ?? ""} key={code ?? ""} position={index}>
              <IndexTable.Cell>
                <InlineStack blockAlign="center" gap={"200"}>
                  <Text as="span" fontWeight="bold" variant="bodyMd">
                    {code}
                  </Text>
                </InlineStack>
              </IndexTable.Cell>
              <IndexTable.Cell>{count}</IndexTable.Cell>
              <IndexTable.Cell>{user_count}</IndexTable.Cell>
              <IndexTable.Cell>{first_injected}</IndexTable.Cell>
              <IndexTable.Cell>{last_injected}</IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      )}
    </Card>
  );
};
